import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export interface VisitorSearchParams {
    firstName: string;
    lastName: string;
    middleName?: string;
    token: string;
    startDate?: string;
    endDate?: string;
}

/**
 * Validate location token
 * POST /selfsign/accessTokens/{token}/validate
 */
export const validateToken = async (token: string): Promise<{ isValid: boolean; err: string | null }> => {
    try {
        const response = await axios.post(`${API_BASE_URL}/selfsign/accessTokens/${token}/validate`);
        const isValid = response.data?.data?.isValid || false;
        return { isValid, err: isValid ? null : 'Invalid token' };
    } catch (error: any) {
        return { isValid: false, err: error.response?.data?.message || 'Token validation failed' };
    }
};

/**
 * Fetch logo content for self sign-in
 * GET /selfsign/logoContent?token={token}
 * Returns logo blob URL or null if not available
 */
export const fetchLogoContent = async (token: string): Promise<string | null> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/selfsign/logoContent`, {
            params: { token },
            responseType: 'blob'
        });

        // Convert blob to URL
        if (response.data) {
            const logoBlob = response.data;
            const logoUrl = URL.createObjectURL(logoBlob);
            return logoUrl;
        }

        return null;
    } catch (error: any) {
        console.error('Failed to fetch logo content:', error);
        return null;
    }
};

/**
 * Search for scheduled visitors by name
 * Returns visits with visitors who have status 1 (Pending) - visitors who haven't signed in yet
 * GET /selfsign/visitor
 */
export const searchScheduledVisitors = async (params: VisitorSearchParams): Promise<any[]> => {
    try {
        const apiParams: any = {
            token: params.token,
            firstName: params.firstName,
            lastName: params.lastName
        };

        if (params.startDate) {
            apiParams.startDate = params.startDate;
        }
        if (params.endDate) {
            apiParams.endDate = params.endDate;
        }

        const response = await axios.get(`${API_BASE_URL}/selfsign/visitor`, {
            params: apiParams
        });

        const allVisits = response.data?.data || [];

        // Filter at visitor level - only include visitors with status 1 (Pending)
        const visitsWithPendingVisitors = allVisits
            .map((visit: any) => ({
                ...visit,
                visitors: visit.visitors?.filter((visitor: any) => visitor.status === 1) || []
            }))
            .filter((visit: any) => visit.visitors.length > 0);

        return visitsWithPendingVisitors;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || 'Failed to search for visitors');
    }
};

/**
 * Search for signed-in visitors by name (for sign out)
 * Returns visits with visitors who have status 2 (Signed In) - visitors who are currently checked in
 * GET /selfsign/visitor
 */
export const searchSignedInVisitors = async (params: VisitorSearchParams): Promise<any[]> => {
    try {
        const apiParams: any = {
            token: params.token,
            firstName: params.firstName,
            lastName: params.lastName
        };

        if (params.startDate) {
            apiParams.startDate = params.startDate;
        }
        if (params.endDate) {
            apiParams.endDate = params.endDate;
        }

        const response = await axios.get(`${API_BASE_URL}/selfsign/visitor`, {
            params: apiParams
        });

        const allVisits = response.data?.data || [];

        // Filter at visitor level - only include visitors with status 2 (Signed In)
        const visitsWithSignedInVisitors = allVisits
            .map((visit: any) => ({
                ...visit,
                visitors: visit.visitors?.filter((visitor: any) => visitor.status === 2) || []
            }))
            .filter((visit: any) => visit.visitors.length > 0);

        return visitsWithSignedInVisitors;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || 'Failed to search for signed-in visitors');
    }
};

/**
 * Check visitor against red flag database
 * GET /selfsign/redFlag
 * Returns detailed red flag data for matching logic
 */
export const checkRedFlag = async (firstName: string, lastName: string, token: string): Promise<any[]> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/selfsign/redFlag`, {
            params: {
                token,
                firstName,
                lastName
            }
        });

        const flagList = response.data?.data;
        return Array.isArray(flagList) ? flagList : [];

    } catch (error: any) {
        throw new Error(error.response?.data?.message || 'Failed to check red flag');
    }
};

/**
 * Check if visitor is red flagged with detailed matching logic
 * Compares names (case-insensitive) and checks expireDate validity
 */
export const isVisitorRedFlagged = (
    firstName: string,
    lastName: string,
    redFlagData: any[]
): boolean => {
    let isMatched = false;

    for (let i = 0; i < redFlagData.length; i++) {
        const flagData = redFlagData[i];

        // Compare first name and last name (case-insensitive)
        if (
            flagData.firstName?.toLowerCase() === firstName.toLowerCase() &&
            flagData.lastName?.toLowerCase() === lastName.toLowerCase()
        ) {
            // Check expireDate
            if (flagData.expireDate && flagData.expireDate.trim() !== '') {
                // Parse expireDate (stored as UTC) and convert to local time for comparison
                const expireDateUtc = dayjs.utc(flagData.expireDate);
                const expireDateLocal = expireDateUtc.local();
                const now = dayjs();

                if (expireDateLocal.isAfter(now)) {
                    // Not expired -> matched
                    isMatched = true;
                    break;
                }
            } else {
                // No expireDate means unlimited -> matched
                isMatched = true;
                break;
            }
        }
    }

    return isMatched;
};

/**
 * Get location agreements
 * GET /selfsign/agreements
 */
export const getLocationAgreements = async (token: string): Promise<any[]> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/selfsign/agreements`, {
            params: { token }
        });

        return response.data?.data || [];
    } catch (error: any) {
        return [];
    }
};

/**
 * Get document content
 * GET /selfsign/documentcontent/{documentId}
 */
export const getDocumentContent = async (documentId: number, token: string): Promise<any> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/selfsign/documentcontent/${documentId}`, {
            params: { token }
        });

        const documents = response.data?.data || [];
        return documents[0] || null;
    } catch (error: any) {
        return null;
    }
};

/**
 * Get workflow in configuration (default settings)
 * GET /selfsign/workflowInConfig
 */
export const getWorkflowInConfig = async (token: string): Promise<any> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/selfsign/workflowInConfig`, {
            params: { token }
        });

        return response.data?.data || null;
    } catch (error: any) {
        return null;
    }
};

/**
 * Get workflow in fields (default field settings)
 * GET /selfsign/workflowInFields
 */
export const getWorkflowInFields = async (token: string): Promise<any> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/selfsign/workflowInFields`, {
            params: { token }
        });

        return response.data?.data?.fieldsIn?.[0] || null;
    } catch (error: any) {
        return null;
    }
};

/**
 * Get workflow in custom fields (default field settings)
 * GET /selfsign/workflowInFieldsCustom
 */
export const getWorkflowInFieldsCustom = async (token: string): Promise<any[]> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/selfsign/workflowInFieldsCustom`, {
            params: { token }
        });

        return response.data?.data || [];
    } catch (error: any) {
        return [];
    }
};

/**
 * Get workflow configuration by visitor type
 * GET /selfsign/workflowByVisitorType
 */
export const getWorkflowByVisitorType = async (token: string): Promise<any[]> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/selfsign/workflowByVisitorType`, {
            params: { token }
        });

        return response.data?.data || [];
    } catch (error: any) {
        return [];
    }
};

/**
 * Get hosts list
 * GET /selfsign/hosts
 */
export const getHosts = async (token: string): Promise<any[]> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/selfsign/hosts`, {
            params: { token }
        });

        return response.data?.data || [];
    } catch (error: any) {
        return [];
    }
};

/**
 * Submit visit
 * POST /selfsign/visit
 */
export const submitVisit = async (visitData: any, token: string): Promise<any> => {
    try {
        const response = await axios.post(
            `${API_BASE_URL}/selfsign/visit`,
            visitData,
            {
                params: { token }
            }
        );

        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || 'Failed to submit visit');
    }
};

/**
 * Update visitor agreement status
 * PATCH /selfsign/visit/{visitId}/agreement
 */
export const updateVisitorAgreement = async (
    visitId: number,
    agreementData: {
        documentId: number;
        visitorId: number[];
        hasAgreed: number;
        hasSigned: number;
        mode?: string;
    },
    token: string
): Promise<any> => {
    try {
        const payload = {
            mode: agreementData.mode || 'append',
            documentId: agreementData.documentId,
            visitorId: agreementData.visitorId,
            hasAgreed: agreementData.hasAgreed,
            hasSigned: agreementData.hasSigned
        };

        const response = await axios.patch(
            `${API_BASE_URL}/selfsign/visit/${visitId}/agreement`,
            payload,
            {
                params: { token }
            }
        );

        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || 'Failed to update agreement');
    }
};

/**
 * Upload visitor photo
 * POST /selfsign/visit/{visitId}/visitor/{visitorId}/photo
 */
export const uploadVisitorPhoto = async (
    visitId: number,
    visitorId: number,
    photoFile: File,
    token: string
): Promise<any> => {
    try {
        const formData = new FormData();
        formData.append('photoUploadFile', photoFile);

        const response = await axios.post(
            `${API_BASE_URL}/selfsign/visit/${visitId}/visitor/${visitorId}/photo`,
            formData,
            {
                params: { token },
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }
        );

        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || 'Failed to upload photo');
    }
};

/**
 * Get custom field data for a visit
 * GET /selfsign/visit/{visitId}/data
 */
export const getVisitData = async (
    visitId: number,
    token: string
): Promise<any[]> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/selfsign/visit/${visitId}/data`, {
            params: { token }
        });

        return response.data?.data || [];
    } catch (error: any) {
        return [];
    }
};

/**
 * Create custom field data for a visit
 * POST /selfsign/visit/{visitId}/data
 */
export const createVisitData = async (
    visitId: number,
    fieldData: { fieldId: number; value: string },
    token: string
): Promise<any> => {
    try {
        const response = await axios.post(
            `${API_BASE_URL}/selfsign/visit/${visitId}/data`,
            fieldData,
            {
                params: { token }
            }
        );

        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || 'Failed to create visit data');
    }
};

/**
 * Update custom field data
 * PATCH /selfsign/visit/{visitId}/data/{dataId}
 */
export const updateCustomFieldData = async (
    visitId: number,
    dataId: number,
    customFieldData: { value: string; fieldId?: number },
    token: string
): Promise<any> => {
    try {
        const response = await axios.patch(
            `${API_BASE_URL}/selfsign/visit/${visitId}/data/${dataId}`,
            customFieldData,
            {
                params: { token }
            }
        );

        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || 'Failed to update custom field data');
    }
};

/**
 * Upload visitor agreement file
 * POST /selfsign/visit/{visitId}/visitor/{visitorId}/agreementFile
 */
export const uploadVisitorAgreementFile = async (
    visitId: number,
    visitorId: number,
    agreementFile: File | Blob,
    token: string
): Promise<any> => {
    try {
        const formData = new FormData();
        formData.append('uploadFile', agreementFile, 'agreements.pdf');

        const response = await axios.post(
            `${API_BASE_URL}/selfsign/visit/${visitId}/visitor/${visitorId}/agreementFile`,
            formData,
            {
                params: { token },
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }
        );

        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || 'Failed to upload agreement file');
    }
};

/**
 * Get CheckrTrust integration info
 * GET /selfsign/integrationCheckrTrust
 */
export const getCheckrTrustIntegration = async (
    token: string
): Promise<any> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/selfsign/integrationCheckrTrust`, {
            params: { token }
        });

        return response.data?.data || null;
    } catch (error: any) {
        return null;
    }
};

/**
 * Check CheckrTrust records
 * POST /selfsign/integrationCheckrTrust/checkRecords
 * Returns check results with criminal records if found
 */
export const checkCheckrTrustRecords = async (
    personData: {
        firstName: string;
        lastName: string;
        middleName?: string;
        dateOfBirth?: string;
    },
    token: string
): Promise<any> => {
    try {
        const response = await axios.post(
            `${API_BASE_URL}/selfsign/integrationCheckrTrust/checkRecords`,
            personData,
            {
                params: { token }
            }
        );

        return response.data?.data || null;
    } catch (error: any) {
        // If integration not found or connection failed, return null (allow check-in)
        if (error.response?.status === 409) {
            return null;
        }
        throw new Error(error.response?.data?.message || 'Failed to check CheckrTrust records');
    }
};

/**
 * Check if visitor has criminal records in CheckrTrust
 * Returns true if criminal records found (should deny access)
 */
export const hasCheckrTrustRecords = (checkData: any): boolean => {
    if (!checkData) {
        return false;
    }

    // Check if results array exists and has records
    if (checkData.results && Array.isArray(checkData.results) && checkData.results.length > 0) {
        return true;
    }

    return false;
};

/**
 * Get visitor types
 * GET /selfsign/visitorType
 */
export const getVisitorTypes = async (
    token: string
): Promise<any[]> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/selfsign/visitorType`, {
            params: { token }
        });

        return response.data?.data || [];
    } catch (error: any) {
        return [];
    }
};
